import React, { useEffect, useRef, useState } from 'react';

import { MtxButton } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';

import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import { Loader } from '@/components/Loader/loader';
import QpsAnswerChoice from '@/components/QpsAnswerChoice/QpsAnswerChoice';
import QpsAnswerFeedback from '@/components/QpsAnswerFeedback/QpsAnswerFeedback';
import QpsAvenantLink from '@/components/QpsAvenantLink/QpsAvenantLink';
import QpsIntroduction from '@/components/QpsIntroduction/QpsIntroduction';
import QpsNotes from '@/components/QpsNotes/QpsNotes';
import QpsQuestionBadge from '@/components/QpsQuestionBadge/QpsQuestionBadge';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { QPS_NOTE_CALLS } from '@/constants/regex';
import { useQps } from '@/hooks/QpsContext';
import useScreenSize from '@/hooks/useScreenSize';
import { isQpsAnswerCorrect } from '@/utils/functions/qps/computeQpsScore';
import trackQps from '@/utils/functions/tracking/trackQps/trackQps';

import styles from './styles.module.css';

import type { BreadcrumbItem } from '@/services/types/Breadcrumb';
import type { QpsScore } from '@/types/qps';

interface QpsQuestionnaireProps {
  codeSupport: string;
  productName: string;
  productIsin: string;
  introDescription?: string | null;
  // Same avenant for every question of the questionnaire, shown on the question screen only.
  avenantReference?: string | null;
  // Set only when the arbitrage has several questionnaires (« Questionnaire 1 sur 2 »).
  rank?: { current: number; total: number };
  // Numéro de la tentative en cours, à partir de 1 (tracking).
  attempt: number;
  skipIntroduction?: boolean;
  breadcrumbItems: BreadcrumbItem[];
  onExit: () => void;
  onComplete: (score: QpsScore) => void;
}

/**
 * Questionnaire de compréhension: introduction, then the current question. The questions, the
 * answers and the current question live in the QPS context. Stored answers skip the introduction
 * (the client resumes on the first unanswered question); `skipIntroduction` opens a new attempt
 * directly on the first question, it is read on mount: the parent remounts through `key`.
 */
const QpsQuestionnaire: React.FC<QpsQuestionnaireProps> = ({
  codeSupport,
  productName,
  productIsin,
  introDescription,
  avenantReference,
  rank,
  attempt,
  skipIntroduction,
  breadcrumbItems,
  onExit,
  onComplete,
}) => {
  const { t } = useTranslation();
  const { arbitrageId, getQuestionnaire, answerQuestion, setQuestionIndex, finishQuestionnaire } = useQps();
  const { windowWidth } = useScreenSize();
  const questionnaire = getQuestionnaire(codeSupport);
  const questions = questionnaire?.questions ?? [];
  const questionIndex = questionnaire?.currentQuestionIndex ?? 0;
  const question = questions.at(questionIndex);
  const [isIntroVisible, setIsIntroVisible] = useState(
    () => !skipIntroduction && !questions.some(({ userRespond }) => userRespond !== undefined)
  );
  const titleRef = useRef<HTMLHeadingElement>(null);
  const isFinishingRef = useRef(false);
  const isDisplayTrackedRef = useRef(false);
  const hasQuestion = question !== undefined;

  useEffect(() => {
    if (!hasQuestion || isDisplayTrackedRef.current) return;
    isDisplayTrackedRef.current = true;
    trackQps('questionnaire_affiche', { codeSupport, attempt });
  }, [hasQuestion]);

  // Changer de question ne change pas de page : sans ce recentrage le focus resterait sur le
  // bouton « Question suivante » démonté, et repartirait du haut du document.
  useEffect(() => {
    if (!isIntroVisible) titleRef.current?.focus();
  }, [question?.id, isIntroVisible]);

  if (!question) return <Loader />;

  const selectedValue = question.userRespond;
  const isAnswered = selectedValue !== undefined;
  const isCorrect = isQpsAnswerCorrect(question, selectedValue);
  const isLastQuestion = questionIndex === questions.length - 1;
  const labelId = `qps-question-label-${question.id}`;
  // Un seul choix dans l'ordre de tabulation (le choisi, sinon le premier) : le groupe se traverse
  // ensuite aux flèches, comme un radiogroup.
  const focusableIndex = Math.max(
    question.answers.findIndex((answer) => answer.value === selectedValue),
    0
  );

  // Appels de note (¹ ²) mis en forme sans changer le texte, qui nomme le groupe de réponses.
  // `start` (position dans le libellé) sert de clé stable.
  let start = 0;
  const labelParts = question.label
    .split(QPS_NOTE_CALLS)
    .map((text, index) => {
      const part = { text, start, isNoteCall: index % 2 === 1 };
      start += text.length;
      return part;
    })
    .filter(({ text }) => text !== '');

  const goBack = () => {
    if (questionIndex > 0) {
      setQuestionIndex(codeSupport, questionIndex - 1);
    } else {
      setIsIntroVisible(true);
    }
  };

  const goNext = () => {
    if (!isLastQuestion) {
      setQuestionIndex(codeSupport, questionIndex + 1);
      return;
    }
    if (isFinishingRef.current) return;
    isFinishingRef.current = true;
    const score = finishQuestionnaire(codeSupport);
    if (score) onComplete(score);
  };

  // Les flèches déplacent le focus sans sélectionner : contrairement à un radiogroup classique,
  // choisir une réponse la valide définitivement (pas de correction possible).
  const moveFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const offsets: Record<string, number> = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
    const offset = offsets[event.key];
    if (offset === undefined) return;

    const radios = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')];
    const current = radios.indexOf(event.target as HTMLButtonElement);
    if (current === -1) return;

    event.preventDefault();
    radios[(current + offset + radios.length) % radios.length].focus();
  };

  return (
    <div className={styles.flow}>
      {/* La flèche mobile recule d'une étape (question précédente, puis introduction) ; le fil
          d'Ariane desktop garde son comportement habituel et ramène à la proposition. */}
      <Breadcrumb
        items={breadcrumbItems}
        alternative
        onBack={windowWidth <= BREAKPOINTS.tablet && !isIntroVisible ? goBack : onExit}
      />

      <div className={styles.column}>
        {isIntroVisible ? (
          <QpsIntroduction
            questionCount={questions.length}
            productName={productName}
            productIsin={productIsin}
            introDescription={introDescription}
            questionnaireIndex={rank?.current}
            questionnaireCount={rank?.total}
            onStart={() => setIsIntroVisible(false)}
            onExit={onExit}
          />
        ) : (
          <div className={styles.screen}>
            <div className={styles.topSection}>
              <header className={styles.header}>
                {rank && (
                  <p className={styles.overline}>
                    {t('qps.question.overlineMulti', { index: rank.current, total: rank.total })}
                  </p>
                )}
                <h1 className={styles.title} ref={titleRef} tabIndex={-1}>
                  {t('qps.question.title', { product: productName })}
                </h1>
              </header>

              <section className={styles.card}>
                <div className={styles.question}>
                  <QpsQuestionBadge current={questionIndex + 1} total={questions.length} />
                  <p className={styles.label} id={labelId}>
                    {labelParts.map(({ text, start: key, isNoteCall }) =>
                      isNoteCall ? (
                        <span key={key} className={styles.noteCall}>
                          {text}
                        </span>
                      ) : (
                        <React.Fragment key={key}>{text}</React.Fragment>
                      )
                    )}
                  </p>
                </div>

                {/* aria-live porté par le conteneur, toujours monté et visible : une région live
                    apparaissant en même temps que son texte n'est pas annoncée. */}
                <div className={styles.answers} aria-live="polite">
                  {/* Focusable par script seulement (-1) : la tabulation entre sur la réponse focusable,
                      le groupe ne fait que recevoir les flèches de ses réponses. */}
                  <div
                    role="radiogroup"
                    aria-labelledby={labelId}
                    tabIndex={-1}
                    className={styles.answerGroup}
                    onKeyDown={moveFocus}
                  >
                    {question.answers.map((answer, answerIndex) => (
                      <QpsAnswerChoice
                        key={answer.id}
                        answer={answer}
                        isSelected={selectedValue === answer.value}
                        isLocked={isAnswered}
                        isFocusable={answerIndex === focusableIndex}
                        showResult={isAnswered}
                        onSelect={(value) => answerQuestion(codeSupport, value)}
                      />
                    ))}
                  </div>

                  {isAnswered && <QpsAnswerFeedback isCorrect={isCorrect} />}
                </div>

                {isAnswered && (
                  <div className={styles.actions}>
                    <MtxButton variant="primary" size="large" fullWidth onClick={goNext}>
                      <span>{t(isLastQuestion ? 'qps.question.results' : 'qps.question.next')}</span>
                    </MtxButton>
                  </div>
                )}
              </section>

              {arbitrageId && avenantReference && (
                <QpsAvenantLink arbitrageId={arbitrageId} codeSupport={codeSupport} reference={avenantReference} />
              )}
            </div>

            <QpsNotes notes={question.notes} />
          </div>
        )}
      </div>
    </div>
  );
};

export default QpsQuestionnaire;
